import React, { useState, useEffect } from 'react';
import { FaFileInvoiceDollar } from 'react-icons/fa';
import { showError, showSuccess, showLoading, closeLoading, confirmarEliminacion } from '../../utils/alerts';
import retencionHonorariosService from '../../services/retencionHonorariosService';
import { eliminarPeriodoCarga } from '../../services/cargasPeriodoService';
import useYearSelector from '../../hooks/useYearSelector';
import useMesesCerrados from '../../hooks/useMesesCerrados';
import '../../styles/pages/CargaCompras.css';

const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const RetencionHonorariosPage = ({ token, id_company }) => {
    const { selectedYear: year, setSelectedYear: setYear, yearOptions } = useYearSelector(null, 10, 1);
    const [files, setFiles] = useState(Array(12).fill(null));
    const [status, setStatus] = useState(Array(12).fill({ cargado: false, procesado: false }));
    const [loadingSii, setLoadingSii] = useState(Array(12).fill(false));
    const mesesCerrados = useMesesCerrados(id_company, year, token);
    const mesCerrado = (index) => !!mesesCerrados[index + 1];
    // Un mes cargado no se vuelve a cargar ni consultar: primero hay que eliminarlo
    const bloqueado = (index) => mesCerrado(index) || status[index].cargado;
    const motivoBloqueo = (index) => mesCerrado(index)
        ? 'Mes cerrado'
        : status[index].cargado ? 'Mes ya cargado: elimínelo para volver a cargarlo' : undefined;

    useEffect(() => {
        if (!year || !token) return;
        const fetchStatus = async () => {
            try {
                const data = await retencionHonorariosService.getEstadoMeses(id_company, year, token);
                const newStatus = Array(12).fill(null).map((_, i) => {
                    const found = data.find(r => r.mes === i + 1);
                    return found
                        ? { cargado: found.cargado, procesado: found.procesado }
                        : { cargado: false, procesado: false };
                });
                setStatus(newStatus);
            } catch (error) {
                console.error('Error obteniendo estado honorarios', error);
            }
        };
        fetchStatus();
    }, [year, id_company, token]);

    const handleFileChange = (index, file) => {
        const newFiles = [...files];
        newFiles[index] = file;
        setFiles(newFiles);
    };

    const handleUpload = async (index) => {
        const file = files[index];
        if (!file || !year) {
            showError('Por favor selecciona un archivo y un año');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('id_company', id_company);
        formData.append('mes', index + 1);
        formData.append('anio', year);

        try {
            // Cargar también contabiliza el período: si algo falla no queda nada guardado
            showLoading('Cargando y contabilizando honorarios', `${MESES[index]} ${year}`);
            const response = await retencionHonorariosService.uploadHonorariosMensuales(formData, token);
            closeLoading();
            showSuccess(response.message);
            setStatus(prev => {
                const updated = [...prev];
                updated[index] = { cargado: true, procesado: true };
                return updated;
            });
            setFiles(prev => {
                const updated = [...prev];
                updated[index] = null;
                return updated;
            });
        } catch (error) {
            closeLoading();
            console.error('Error al cargar archivo', error);
            showError('No se cargó el archivo', error.message || 'Error al cargar el archivo');
        }
    };

    const consultarSii = async (index) => {
        if (!year) {
            showError('Por favor selecciona un año');
            return;
        }

        setLoadingSii(prev => {
            const updated = [...prev];
            updated[index] = true;
            return updated;
        });

        try {
            const response = await retencionHonorariosService.consultarSiiHonorarios(
                id_company, parseInt(year), index + 1, token
            );

            if (response.success) {
                showSuccess(`Honorarios de ${MESES[index]} obtenidos del SII y contabilizados`);
                setStatus(prev => {
                    const updated = [...prev];
                    updated[index] = { cargado: true, procesado: true };
                    return updated;
                });
            } else {
                showError(response.error || 'Error en la consulta');
            }
        } catch (error) {
            console.error('Error al consultar SII:', error);
            showError(error.message || 'Error al consultar el SII');
        } finally {
            setLoadingSii(prev => {
                const updated = [...prev];
                updated[index] = false;
                return updated;
            });
        }
    };

    // Deshace la carga del mes: borra sus boletas y los asientos que generaron, para volver
    // a cargar o consultar desde cero. La fila de un mes cerrado está deshabilitada.
    const handleEliminar = async (index) => {
        const confirmado = await confirmarEliminacion(
            `¿Eliminar los honorarios de ${MESES[index]} ${year}?`,
            'Se eliminarán:<br><br>• Las boletas de honorarios del mes<br>• Sus asientos contables (devengo y pago al profesional)<br><br>Después podrás volver a cargar o consultar el mes.'
        );
        if (!confirmado) return;

        try {
            const response = await eliminarPeriodoCarga('honorarios', id_company, year, index + 1, token);
            showSuccess(response.message);
            setStatus(prev => {
                const updated = [...prev];
                updated[index] = { cargado: false, procesado: false };
                return updated;
            });
        } catch (error) {
            console.error('Error al eliminar el período:', error);
            showError(error.response?.data?.error || 'Error al eliminar los honorarios del mes');
        }
    };

    const mesConsultando = loadingSii.findIndex(v => v);

    return (
        <div className="carga-compras">
            {loadingSii.some(v => v) && (
                <div className="sii-loading-overlay">
                    <div className="sii-loading-modal">
                        <div className="sii-loading-spinner"></div>
                        <h3>Consultando SII</h3>
                        {mesConsultando >= 0 && <p>{MESES[mesConsultando]}</p>}
                        <small>Por favor espere, no cierre esta ventana</small>
                    </div>
                </div>
            )}

            <h1><FaFileInvoiceDollar style={{ marginRight: 8 }} />Retención de Honorarios</h1>

            <label htmlFor="yearSelect">Selecciona un Año:</label>
            <select
                id="yearSelect"
                value={year}
                onChange={(e) => setYear(e.target.value)}
            >
                <option value="">Selecciona un año</option>
                {yearOptions.map(y => (
                    <option key={y.value} value={y.value}>{y.label}</option>
                ))}
            </select>

            <table>
                <thead>
                    <tr>
                        <th>Mes</th>
                        <th>Archivo</th>
                        <th>Acción</th>
                        <th>Cargado</th>
                        <th>Procesado</th>
                        <th>Consultar SII</th>
                        <th>Eliminar</th>
                    </tr>
                </thead>
                <tbody>
                    {MESES.map((mes, index) => (
                        <tr key={mes}>
                            <td>{mes}{mesCerrado(index) && ' (Cerrado)'}</td>
                            <td>
                                <input
                                    type="file"
                                    accept=".xls,.xlsx"
                                    onChange={(e) => handleFileChange(index, e.target.files[0])}
                                    disabled={bloqueado(index)}
                                />
                            </td>
                            <td>
                                <button
                                    onClick={() => handleUpload(index)}
                                    disabled={bloqueado(index)}
                                    title={motivoBloqueo(index)}
                                >
                                    Cargar
                                </button>
                            </td>
                            <td className={status[index].cargado ? 'status-true' : ''}>
                                {status[index].cargado ? 'Sí' : 'No'}
                            </td>
                            <td className={status[index].procesado ? 'status-true' : ''}>
                                {status[index].procesado ? 'Sí' : 'No'}
                            </td>
                            <td>
                                <button
                                    onClick={() => consultarSii(index)}
                                    disabled={loadingSii[index] || bloqueado(index)}
                                    title={motivoBloqueo(index)}
                                >
                                    {loadingSii[index] ? 'Consultando...' : 'Consultar SII'}
                                </button>
                            </td>
                            <td>
                                <button
                                    onClick={() => handleEliminar(index)}
                                    disabled={mesCerrado(index) || !status[index].cargado}
                                    title={mesCerrado(index) ? 'Mes cerrado' : 'Elimina las boletas del mes y sus asientos contables'}
                                >
                                    Eliminar
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default RetencionHonorariosPage;
